import React, { useCallback, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import TaxonomyReviewActions from './TaxonomyReviewActions';
import {
  familyLabel,
  humanize,
  normalizeValue,
  recommendationForRow,
  rowIssueTypes,
  suggestedCanonicalForRow,
} from './taxonomyLibrarianUtils';


export default function TaxonomyReviewTable({ rows, actions, onOpenPlace }) {
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const syncingScrollRef = useRef(false);
  const { cleaningQueueId, reviewingQueueId, removeCurrentRows } = actions;
  const busyRow = row => reviewingQueueId === row.queue_id || cleaningQueueId === row.queue_id;

  const syncHorizontalScroll = useCallback(source => {
    const from = source === 'top' ? topScrollRef.current : tableScrollRef.current;
    const to = source === 'top' ? tableScrollRef.current : topScrollRef.current;
    if (!from || !to || syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    to.scrollLeft = from.scrollLeft;
    window.requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }, []);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div
        ref={topScrollRef}
        onScroll={() => syncHorizontalScroll('top')}
        className="overflow-x-auto border-b border-gray-100 bg-gray-50/70"
        aria-label="Scroll taxonomy review table horizontally"
      >
        <div className="h-3 min-w-[1040px]" />
      </div>
      <div
        ref={tableScrollRef}
        onScroll={() => syncHorizontalScroll('table')}
        className="overflow-x-auto"
      >
        <table className="min-w-[1040px] w-full table-fixed text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="w-20 px-3 py-2.5 text-left font-medium">Priority</th>
              <th className="w-40 px-3 py-2.5 text-left font-medium">Term</th>
              <th className="px-3 py-2.5 text-left font-medium">Finding</th>
              <th className="w-64 px-3 py-2.5 text-left font-medium">Recommendation</th>
              <th className="w-52 px-3 py-2.5 text-left font-medium">Action</th>
              <th className="w-44 px-3 py-2.5 text-left font-medium">Context</th>
              <th className="w-12 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(row => {
              const target = suggestedCanonicalForRow(row);
              const issueTypes = rowIssueTypes(row);
              const issueCount = Number(row.issue_count || row.usage || 1);
              const recommendation = recommendationForRow(row);
              return (
                <tr key={row.queue_id} className="align-top hover:bg-gray-50">
                  <td className="px-3 py-3 tabular-nums text-gray-500">{row.priority}</td>
                  <td className="px-3 py-3">
                    <p className="break-words font-mono text-xs font-medium text-gray-900">{row.tag}</p>
                    <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">{familyLabel(row.family)}</p>
                    {row.raw_value && normalizeValue(row.raw_value) !== normalizeValue(row.tag) && (
                      <p className="mt-1 break-words text-xs text-gray-500">raw: {row.raw_value}</p>
                    )}
                    {target && <p className="mt-1 break-words text-xs text-gray-500">to {target}</p>}
                  </td>
                  <td className="px-3 py-3">
                    <p className="font-medium text-gray-800">
                      {issueTypes.length > 1 ? `${issueCount} findings` : humanize(row.issue_type || row.review_type)}
                    </p>
                    {issueTypes.length > 1 && (
                      <div className="mt-1 flex max-w-xl flex-wrap gap-1">
                        {issueTypes.map(issueType => (
                          <span key={issueType} className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                            {humanize(issueType)}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="mt-1 max-w-xl text-xs leading-5 text-gray-500">{row.reason}</p>
                  </td>
                  <td className="px-3 py-3 text-xs leading-5">
                    <p className="font-medium text-gray-800">{recommendation.title}</p>
                    <p className="mt-1 text-gray-500">{recommendation.detail}</p>
                    {recommendation.bulkAction && (
                      <span className="mt-2 inline-flex rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                        Safe bulk
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs leading-5 text-gray-700">
                    <p>{humanize(row.recommended_action)}</p>
                    {row.recommended_action === 'remove_literal_yes_from_current_rows' && row.place_master_id && (
                      <button
                        type="button"
                        onClick={() => removeCurrentRows(row)}
                        disabled={busyRow(row)}
                        className="mt-2 inline-flex h-8 items-center rounded-md border border-red-200 bg-red-50 px-2.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                      >
                        {cleaningQueueId === row.queue_id ? 'Removing...' : 'Remove current rows'}
                      </button>
                    )}
                    <TaxonomyReviewActions row={row} actions={actions} />
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-500">
                    <p className="break-words">{row.source_table || (row.usage ? `${row.usage} uses` : '-')}</p>
                    {issueCount > 1 && <p className="mt-1">{issueCount} findings</p>}
                    {row.examples && <p className="mt-1 break-words">{row.examples}</p>}
                    {row.place_master_id && <p className="mt-1 break-words font-mono">{row.place_master_id}{row.card_key ? ` / ${row.card_key}` : ''}</p>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {row.place_master_id && (
                      <button
                        type="button"
                        onClick={() => onOpenPlace?.(row.place_master_id, row.card_key || 'primary')}
                        aria-label={`Open ${row.place_master_id}`}
                        title="Open place card"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <p className="py-10 text-center text-sm text-gray-400">No matching review items</p>}
    </div>
  );
}
